import {Module} from '@mamdev/cad.static-next-lib';
import {AjaxPost} from '../../ts/services/ajax-post';
import Viewport from '../../ts/services/viewport';

interface Selectors {
  toolContent: string;
  voteContent: string;
  questions: string;
  questionsOption: string;
  responseError: string;
  questionNumber: string;
  questionPercentage: string;
  resultOverall: string;
}

interface ResponseData {
  totalVotes: number;
  answers: ResponseAnswer[];
}

type ResponseAnswer = Record<string, number>;

export default class Voting extends Module {
  selectors: Selectors;

  timeouts: {
    resultAnimation: number;
    prepareDom: number;
    displayResults: number;
    handleTrackingAndAds: number;
  };

  toolContent: HTMLElement | null;
  voteContent: HTMLElement | null;
  responseError: HTMLElement | null;
  questionsOptions: NodeListOf<HTMLElement>;
  resultOverall: HTMLElement | null;

  clickRefs: OmitThisParameter<(event: Event) => void>[];
  responseData: ResponseData;

  // Init. from DOM.
  static attachTo(root: HTMLElement) {
    return new Voting(root);
  }

  constructor(root: HTMLElement) {
    super(root);

    this.name_ = 'voting';

    this.selectors = {
      toolContent: '.tool-content',
      voteContent: '.vote-content',
      questions: '.questions',
      questionsOption: '.questions-option',
      responseError: '.response.error',
      questionNumber: '.question-number',
      questionPercentage: '.question-percentage',
      resultOverall: '.result-overall'
    };

    this.timeouts = {
      resultAnimation: 2000,
      prepareDom: 100,
      displayResults: 200,
      handleTrackingAndAds: 0
    };

    this.timeouts.handleTrackingAndAds = this.timeouts.resultAnimation + this.timeouts.prepareDom + this.timeouts.displayResults;
  }

  start() {
    try {
      // Get all relevant DOM selectors.
      this.setupSelectors();

      // Setup all click handlers for the module and handle the clicks.
      this.setupClickHandlers();
    } catch (e) {
      this.logUnknownError(e);
    }
  }

  /** Setup module-wide needed DOM selectors. */
  setupSelectors() {
    this.toolContent = this.root_.querySelector<HTMLElement>(this.selectors.toolContent);
    this.resultOverall = this.root_.querySelector<HTMLElement>(this.selectors.resultOverall);

    if (this.toolContent) {
      this.voteContent = this.toolContent.querySelector(this.selectors.voteContent);
      this.responseError = this.toolContent.querySelector(this.selectors.responseError);

      if (this.voteContent) {
        this.questionsOptions = this.voteContent.querySelectorAll(this.selectors.questionsOption);
      }
    }
  }

  /** Setup click handlers on all possible answers within the voting. */
  setupClickHandlers() {
    this.clickRefs = [];
    // Iterate over all questionsOptions and add event listener.
    for (const clickedQuestion of this.questionsOptions) {
      const handleClickRef = this.handleClick.bind(this);
      clickedQuestion.addEventListener('click', handleClickRef);
      this.clickRefs.push(handleClickRef);
    }
  }

  /**
   * Setup click handler on a specific DOM node.
   * Note that the callback function triggers the
   * @param {object} event  The triggered event listener event
   */
  handleClick(event: Event) {
    try {
      const votingUrl = this.root_.dataset.votingurl ?? '';
      const answerid = (event.currentTarget as HTMLElement).dataset.answerid ?? '';
      const brainZone = (event.currentTarget as HTMLElement).dataset.brainZone;
      const targetElement = event.currentTarget as HTMLElement;

      // Unlisten all event listeners within scope of module instance
      this.unlistenAllClicks();

      AjaxPost.postJSON(votingUrl, {answerid: parseInt(answerid)})
        .then(response => {
          this.responseData = response as ResponseData;
          // Prepare for the results display
          this.prepareDom(targetElement);
          // Display and animate the results
          this.displayResults();
        })
        .catch(e => {
          this.logUnknownError(e);
          // Error handling for any response status code >= 300
          this.showErrorMessage();
        });

      // Send PI info and trigger adrefresh
      this.handleTrackingAndAds(brainZone);
    } catch (e) {
      this.logUnknownError(e);
    }
  }

  /**
   * Prepare the DOM for displaying the results:
   * Hide the vote content, do the setup, and display the vote content again.
   * @param {object} targetElement  The triggered element
   */
  prepareDom(targetElement: HTMLElement) {
    // 1. Hide voteContent
    this.voteContent?.classList.remove('active');
    this.voteContent?.classList.add('inactive');

    // 2. Wait for the voteContent inactive css animation to be finished, then show results
    setTimeout(() => {
      // Swap voteContent class
      this.voteContent?.classList.remove('questions');
      this.voteContent?.classList.add('results');

      // Highlight selected answer
      targetElement.classList.add('choice');

      if (this.resultOverall) {
        // Update data in result overall
        this.resultOverall.innerText = (this.responseData.totalVotes != null)
          ? this.responseData.totalVotes + ' haben abgestimmt'
          : '';

        // Show result overall in tool-footer
        this.resultOverall.classList.remove('inactive');
        this.resultOverall.classList.add('active');
      }

      // Display voteContent
      this.voteContent?.classList.remove('inactive');
      this.voteContent?.classList.add('active');
    }, this.timeouts.prepareDom);
  }

  /**
   * Display results.
   * Updates questionPercentage width in the DOM, triggers questionNumber animation for all answers.
   */
  displayResults() {
    // Wait for all the DOM preparation to be finished, then display results
    setTimeout(() => {
      // Loop through this.questionsOptions and update each with the corresponding result
      for (let i = 0; i < this.questionsOptions.length; i++) {
        // Find correct object in answers array by id
        // We do this so we don't have to depend on the correct order of answers we receive from the REST service.
        const resultObj = this.getObjectInArray(this.responseData.answers, 'id', i + 1);

        // Update values
        if (resultObj != null) {
          const questionPercentage = this.questionsOptions[i].querySelector<HTMLElement>(this.selectors.questionPercentage);
          const questionNumber = this.questionsOptions[i].querySelector<HTMLElement>(this.selectors.questionNumber);

          if (questionPercentage && questionNumber) {
            const percent = resultObj.percent;

            // Trigger percentage bar animation (css transformation)
            questionPercentage.style.width = percent + '%';

            // Start percent animation
            let animatingPercent = 0;
            const interval = setInterval(() => {
              if (animatingPercent <= percent) questionNumber.innerText = animatingPercent + '%';
              if (animatingPercent >= percent) clearInterval(interval);
              animatingPercent++;
            }, Math.max(this.timeouts.resultAnimation / 100, 100 - percent) - 1);
          }
        }
      }
    }, this.timeouts.displayResults);
  }

  /**
   * Unlisten all click handlers within the module's context.
   */
  unlistenAllClicks() {
    // Iterate over all clickRefs and unlisten the event listener.
    for (let i = 0; i < this.clickRefs.length; i++) {
      const clickedQuestion = this.questionsOptions[i];
      clickedQuestion.removeEventListener('click', this.clickRefs[i]);
    }
    this.clickRefs = [];
  }

  /**
   * Helper function: Return the object from array of objects where key meets value, e.g. id = 1.
   * @param {Array} arr           The array to search in
   * @param {string} key          The key to search for
   * @param {*} value             The value the key should match
   * @return {object|undefined}   Returns the wanted object or undefined if not found
   */
  getObjectInArray(arr: ResponseAnswer[], key: string, value: unknown): ResponseAnswer | undefined {
    for (const o of arr) {
      if (o[key] === value) {
        return o;
      }
    }
  }

  /**
   * Handle ajax errors: hide vote content, display error message instead.
   */
  showErrorMessage() {
    this.voteContent?.classList.add('inactive');

    this.responseError?.classList.remove('hide');
    this.responseError?.classList.remove('inactive');
    this.responseError?.classList.add('show');
    this.responseError?.classList.add('active');
  }

  /**
   * Send PI info and trigger adrefresh.
   * @param {string} brainZone  String parsed from data-brain-zone
   */
  handleTrackingAndAds(brainZone: string | undefined) {
    // Wait for all the DOM preparation to be finished, then display results
    setTimeout(() => {
      // Send PI info
      this.publish('votingtool:setPixel', this.getTrackingParams(brainZone));

      // Trigger adrefresh
      this.publish('ad:refreshAdMedRectInTab', {});
      this.publish('ad:refreshAdSky', {});

      if (Viewport.isMobile()) {
        this.publish('ad:refreshAdTop', {});
      }
    }, this.timeouts.handleTrackingAndAds);
  }

  /**
   * Creates an object with tracking parameters from a given string (from CAE via data-brainzone).
   * @param {string} brainZone  The string to parse
   * @return {object}          Returns tracking object
   */
  getTrackingParams(brainZone: string | undefined) {
    let piUrl;

    if (brainZone != null) {
      const brainZoneArray = brainZone.split('.');

      piUrl = {
        referrer: '&referrer=undefined',
        click1: '&click1=' + brainZoneArray[1],
        click2: '&click2=' + brainZoneArray[2],
        click3: '&click3=' + brainZoneArray[3],
        click4: '&click4=' + brainZoneArray[4]
      };
    } else {
      this.logError('brainZone not specified.');

      piUrl = {
        referrer: '&referrer=undefined',
        click1: '&click1=undefined',
        click2: '&click2=undefined',
        click3: '&click3=undefined',
        click4: '&click4=undefined'
      };
    }

    return piUrl;
  }
}
